/**
 * Tenant wire ⇄ domain mappers.
 *
 * The security-service `TenantResponse` is a flat snake_case object that maps
 * 1:1 to the UI camelCase `Tenant` shape, so the generic deep converter handles
 * reads. For writes, `UpdateTenantInput` is converted to snake_case for the
 * `TenantUpdateRequest` body.
 *
 * `firm_address` and `settings` are part of the read contract, so saved firm
 * settings round-trip through the same generic conversion.
 */

import { toCamelDeep, toSnakeDeep } from '@/services/http/caseMap'
import type {
  BillingOverview,
  EffectiveEntitlements,
  SubscriptionCommandResult,
  Tenant,
  Subscription,
  UpdateTenantInput,
} from '@/types/tenant.types'

export function mapTenantFromApi(raw: Record<string, unknown>): Tenant {
  const t = toCamelDeep<Record<string, unknown>>(raw)
  // The wire PK is `tenant_id`; the UI keys tenants by `tenantId` (already
  // produced by the generic pass). Belt-and-braces in case the backend ever
  // returns `id` instead.
  if (t.tenantId == null && t.id != null) t.tenantId = t.id
  // JSON is recorded data; only the enclosing DTO keys use UI casing.
  t.settings = raw.settings === undefined ? null : raw.settings
  t.firmAddress = Object.prototype.hasOwnProperty.call(raw, 'firm_address')
    ? raw.firm_address
    : raw.firmAddress
  if (Object.prototype.hasOwnProperty.call(raw, 'metadata')) t.metadata = raw.metadata
  return t as unknown as Tenant
}

export function mapSubscriptionFromApi(raw: Record<string, unknown>): Subscription {
  return toCamelDeep<Subscription>(raw)
}

export function mapEffectiveEntitlementsFromApi(
  raw: Record<string, unknown>
): EffectiveEntitlements {
  return toCamelDeep<EffectiveEntitlements>(raw)
}

export function mapBillingOverviewFromApi(raw: Record<string, unknown>): BillingOverview {
  return toCamelDeep<BillingOverview>(raw)
}

export function mapSubscriptionCommandFromApi(
  raw: Record<string, unknown>
): SubscriptionCommandResult {
  return toCamelDeep<SubscriptionCommandResult>(raw)
}

/**
 * Build the `TenantUpdateRequest` body. Only the fields the backend schema
 * accepts (`tenant_name`, `firm_*`, `firm_address`, `settings`) are forwarded.
 */
export function mapUpdateToApi(patch: UpdateTenantInput): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  if (patch.tenantName !== undefined) body.tenant_name = patch.tenantName
  if (patch.firmEmail !== undefined) body.firm_email = patch.firmEmail
  if (patch.firmPhone !== undefined) body.firm_phone = patch.firmPhone
  if (patch.firmGstin !== undefined) body.firm_gstin = patch.firmGstin
  if (patch.firmPan !== undefined) body.firm_pan = patch.firmPan
  if (patch.firmAddress !== undefined) body.firm_address = patch.firmAddress
  if (patch.settings !== undefined) body.settings = toSnakeDeep(patch.settings)
  return body
}
